/**
 * 見積書 / 請求書の「メール送信」編集モーダル（共通）。
 *
 * フロー:
 *  1. open 時に prepare Callable で初期値ロード（送信元=カテゴリ既定 / 件名・本文=テンプレ / 公開URL）
 *  2. 送信元: mail_accounts(active) から選択（既定=prepare値）
 *  3. CC: チップ入力。入力中に mail_contacts を背景検索しサジェスト
 *  4. 件名 / 本文: 編集可能
 *  5. 本文に挿入予定の書類PDFリンクを表示
 *  6. 送信ボタン → 送信元の値からメール送信
 */
import { Copy, Loader2, Mail, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label, Textarea } from '@/components/ui/input';
import { useMailAccounts } from '@/hooks/useMailAccounts';
import { useMailContacts } from '@/hooks/useMailContacts';
import {
  usePrepareQuoteMail,
  useSendQuoteMail,
  type MailAccountOption,
} from '@/hooks/useQuotes';
import {
  usePrepareInvoiceMail,
  useSendInvoiceMail,
} from '@/hooks/useSalesOps';

export type DocumentMailType = 'quote' | 'invoice';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  documentType: DocumentMailType;
  dealId: string;
  /** quoteId または invoiceId */
  documentId: string | null;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function DocumentMailDialog({
  open,
  onOpenChange,
  documentType,
  dealId,
  documentId,
}: Props) {
  const isQuote = documentType === 'quote';
  const label = isQuote ? '見積書' : '請求書';

  const { data: accounts } = useMailAccounts(dealId);
  const { data: contacts } = useMailContacts();

  const prepareQuote = usePrepareQuoteMail();
  const prepareInvoice = usePrepareInvoiceMail();
  const sendQuote = useSendQuoteMail();
  const sendInvoice = useSendInvoiceMail();

  const [loading, setLoading] = useState(false);
  const [to, setTo] = useState('');
  const [fromAccountId, setFromAccountId] = useState('');
  const [ccList, setCcList] = useState<string[]>([]);
  const [ccInput, setCcInput] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [publicUrl, setPublicUrl] = useState('');
  const [showSuggest, setShowSuggest] = useState(false);
  // 外部SA など、サーバが送信元を限定して返した場合の候補（null = 制限なし）
  const [restrictedAccounts, setRestrictedAccounts] = useState<
    MailAccountOption[] | null
  >(null);

  const activeAccounts = useMemo(
    () => (accounts ?? []).filter((a) => a.status === 'active'),
    [accounts],
  );

  // 表示する送信元候補: サーバ側で限定されていればそれを優先する。
  const fromOptions = useMemo<MailAccountOption[]>(
    () =>
      restrictedAccounts ??
      activeAccounts.map((a) => ({
        id: a.id,
        displayName: a.displayName,
        email: a.email,
      })),
    [restrictedAccounts, activeAccounts],
  );

  // open 時に prepare で初期値ロード
  const loadedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!open || !documentId) return;
    const key = `${documentType}:${dealId}:${documentId}`;
    if (loadedRef.current === key) return;
    loadedRef.current = key;
    setLoading(true);
    const run = isQuote
      ? prepareQuote.mutateAsync({ dealId, quoteId: documentId })
      : prepareInvoice.mutateAsync({ dealId, invoiceId: documentId });
    run
      .then((r) => {
        setTo(r.defaultTo ?? '');
        setRestrictedAccounts(r.fromAccountOptions ?? null);
        setFromAccountId(r.fromAccountId ?? '');
        setSubject(r.subject ?? '');
        setBody(r.body ?? '');
        setPublicUrl(r.publicUrl ?? '');
        setCcList([]);
        setCcInput('');
      })
      .catch((err) => {
        toast.error(`メール内容の取得に失敗しました: ${(err as Error).message}`);
        onOpenChange(false);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, documentId, dealId, documentType]);

  // close 時にロード済みフラグをリセット（次回開いたとき再取得）
  useEffect(() => {
    if (!open) loadedRef.current = null;
  }, [open]);

  // 選択中の送信元が候補に無い場合は先頭に寄せる。
  // (プルダウンの表示と実際に送信されるアカウントの食い違いを防ぐ)
  useEffect(() => {
    if (fromOptions.length === 0) return;
    if (fromOptions.some((a) => a.id === fromAccountId)) return;
    setFromAccountId(fromOptions[0]!.id);
  }, [fromOptions, fromAccountId]);

  // CC サジェスト候補（mail_contacts を背景検索）
  const suggestions = useMemo(() => {
    const q = ccInput.trim().toLowerCase();
    if (!q) return [];
    const seen = new Set(ccList.map((e) => e.toLowerCase()));
    const out: { email: string; name: string }[] = [];
    for (const c of contacts ?? []) {
      const emails = [
        c.defaultEmail,
        // c.emails は { address, addedAt } オブジェクトの配列なので address を取り出す
        ...(Array.isArray(c.emails) ? c.emails.map((e) => e.address) : []),
      ].filter((e): e is string => !!e);
      for (const email of emails) {
        const el = email.toLowerCase();
        if (seen.has(el)) continue;
        const name = c.name ?? '';
        if (el.includes(q) || name.toLowerCase().includes(q)) {
          out.push({ email, name });
          seen.add(el);
        }
      }
      if (out.length >= 8) break;
    }
    return out.slice(0, 8);
  }, [ccInput, contacts, ccList]);

  const addCc = (email: string) => {
    const e = email.trim();
    if (!e) return;
    if (!EMAIL_RE.test(e)) {
      toast.error(`メールアドレスの形式が正しくありません: ${e}`);
      return;
    }
    if (ccList.some((x) => x.toLowerCase() === e.toLowerCase())) {
      setCcInput('');
      return;
    }
    setCcList((prev) => [...prev, e]);
    setCcInput('');
    setShowSuggest(false);
  };

  const removeCc = (email: string) => {
    setCcList((prev) => prev.filter((x) => x !== email));
  };

  const handleSend = async () => {
    if (!documentId) return;
    if (!to.trim()) {
      toast.error('宛先を入力してください');
      return;
    }
    // 未確定の CC 入力（Enter/カンマ未押下のまま送信ボタンを押した場合）も CC に含める。
    // これを行わないと、入力しただけの CC が黙って破棄され「CC が届かない」不具合になる。
    const pendingCc = ccInput.trim();
    let effectiveCc = ccList;
    if (pendingCc) {
      if (!EMAIL_RE.test(pendingCc)) {
        toast.error(`CC のメールアドレス形式が正しくありません: ${pendingCc}`);
        return;
      }
      if (!ccList.some((x) => x.toLowerCase() === pendingCc.toLowerCase())) {
        effectiveCc = [...ccList, pendingCc];
      }
    }
    try {
      const overrides = {
        to: to.trim(),
        fromAccountId: fromAccountId || null,
        cc: effectiveCc,
        subject,
        body,
      };
      const r = isQuote
        ? await sendQuote.mutateAsync({
            dealId,
            quoteId: documentId,
            ...overrides,
          })
        : await sendInvoice.mutateAsync({
            dealId,
            invoiceId: documentId,
            ...overrides,
          });
      if (r.mailSent) {
        toast.success(`${label}メールを送信しました`);
        onOpenChange(false);
      } else {
        toast.error(`送信できませんでした: ${r.reason ?? '理由不明'}`);
      }
    } catch (err) {
      toast.error(`送信失敗: ${(err as Error).message}`);
    }
  };

  const sending = sendQuote.isPending || sendInvoice.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{label}をメール送信</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12 text-[var(--text-muted)]">
            <Loader2 className="size-5 animate-spin mr-2" />
            メール内容を読み込み中…
          </div>
        ) : (
          <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
            {/* 送信元 */}
            <div>
              <Label className="text-xs">送信元</Label>
              <select
                value={fromAccountId}
                onChange={(e) => setFromAccountId(e.target.value)}
                className="flex h-9 w-full rounded-sm border border-[var(--border)] bg-[var(--bg-input)] px-3 text-[13px] text-[var(--text-primary)] focus:border-[var(--border-focus)] focus:outline-none"
              >
                {fromOptions.length === 0 && (
                  <option value="">送信可能なアカウントがありません</option>
                )}
                {fromOptions.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.displayName ? `${a.displayName} <${a.email}>` : a.email}
                  </option>
                ))}
              </select>
            </div>

            {/* 宛先 */}
            <div>
              <Label className="text-xs">宛先</Label>
              <Input
                type="email"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                placeholder="customer@example.com"
              />
            </div>

            {/* CC */}
            <div className="relative">
              <Label className="text-xs">CC</Label>
              <div className="flex flex-wrap gap-1 rounded-sm border border-[var(--border)] bg-[var(--bg-input)] p-1.5 focus-within:border-[var(--border-focus)]">
                {ccList.map((email) => (
                  <span
                    key={email}
                    className="inline-flex items-center gap-1 rounded-sm bg-[var(--bg-elevated)] px-2 py-0.5 text-xs text-[var(--text-secondary)]"
                  >
                    {email}
                    <button
                      type="button"
                      onClick={() => removeCc(email)}
                      className="text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                    >
                      <X className="size-3" />
                    </button>
                  </span>
                ))}
                <input
                  value={ccInput}
                  onChange={(e) => {
                    setCcInput(e.target.value);
                    setShowSuggest(true);
                  }}
                  onFocus={() => setShowSuggest(true)}
                  onBlur={() => {
                    setTimeout(() => setShowSuggest(false), 150);
                    // フォーカスが外れたら、有効な未確定 CC 入力をチップ化（確定し忘れ対策）
                    const pending = ccInput.trim();
                    if (pending && EMAIL_RE.test(pending)) addCc(pending);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ',') {
                      e.preventDefault();
                      addCc(ccInput);
                    } else if (
                      e.key === 'Backspace' &&
                      !ccInput &&
                      ccList.length > 0
                    ) {
                      removeCc(ccList[ccList.length - 1]!);
                    }
                  }}
                  placeholder={ccList.length === 0 ? 'CCを追加（入力で候補表示）' : ''}
                  className="flex-1 min-w-[140px] bg-transparent px-1 text-[13px] text-[var(--text-primary)] focus:outline-none"
                />
              </div>
              {showSuggest && suggestions.length > 0 && (
                <ul className="absolute z-50 mt-1 w-full max-h-48 overflow-y-auto rounded-sm border border-[var(--border)] bg-[var(--bg-surface)] shadow-lg">
                  {suggestions.map((s) => (
                    <li key={s.email}>
                      <button
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          addCc(s.email);
                        }}
                        className="flex w-full flex-col items-start px-3 py-1.5 text-left hover:bg-[var(--bg-hover)]"
                      >
                        <span className="text-[13px] text-[var(--text-primary)]">
                          {s.email}
                        </span>
                        {s.name && (
                          <span className="text-xs text-[var(--text-muted)]">
                            {s.name}
                          </span>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* 件名 */}
            <div>
              <Label className="text-xs">件名</Label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </div>

            {/* 本文 */}
            <div>
              <Label className="text-xs">本文</Label>
              <Textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={12}
                className="font-mono text-xs"
              />
            </div>

            {/* PDFリンク */}
            {publicUrl && (
              <div>
                <Label className="text-xs">
                  本文に挿入される書類PDFリンク
                </Label>
                <div className="flex items-center gap-2 rounded-sm border border-[var(--border)] bg-[var(--bg-elevated)] px-3 py-2">
                  <span className="flex-1 truncate text-xs text-[var(--text-secondary)]">
                    {publicUrl}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(publicUrl);
                      toast.success('リンクをコピーしました');
                    }}
                    className="text-[var(--text-muted)] hover:text-[var(--accent)]"
                    title="リンクをコピー"
                  >
                    <Copy className="size-3.5" />
                  </button>
                </div>
                {!body.includes(publicUrl) && (
                  <p className="mt-1 text-xs text-[var(--accent-amber)]">
                    ※ 本文にリンクが含まれていません。必要なら本文に貼り付けてください。
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={sending}
          >
            キャンセル
          </Button>
          <Button
            variant="primary"
            onClick={handleSend}
            disabled={loading || sending || !documentId}
          >
            <Mail className="size-3.5" />
            {sending ? '送信中…' : '送信'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
